import { useMemo, useState } from "react";

import { AlertTriangle } from "lucide-react";

import {
  DataTable,
  type DataTableColumn,
  type DataTableSort,
} from "@/components/cinch/DataTable/DataTable";
import { EntityDrawer } from "@/components/cinch/EntityDrawer";
import { LinkOut } from "@/components/cinch/LinkOut";
import { StatusBadge } from "@/components/cinch/StatusBadge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { fmtDateTime } from "@/lib/date";
import { MarkdownView } from "@/lib/markdown";

import type { KbRagSearch } from "..";
import {
  useKbArticle,
  useKbArticles,
  useKbOverview,
  type ArticleRow,
} from "../api";
import { CategorySelect } from "./CategorySelect";

const ANY = "__any__";
const PAGE = 50;

/** Article sync statuses the pipeline writes, in worst-first order for the
 *  filter. Anything else the pipeline invents still renders (StatusBadge
 *  falls back), it just is not offered as a filter until it is seen. */
const KNOWN_STATUSES = [
  "indexed",
  "changed_pending",
  "pending_index",
  "deleted_pending_purge",
];

function day(iso: string | null): string {
  return iso ? iso.slice(0, 10) : "—";
}

function sortRows(
  rows: ArticleRow[],
  sort: DataTableSort | null,
): ArticleRow[] {
  if (!sort) return rows;
  const dir = sort.dir === "asc" ? 1 : -1;
  const key = sort.key as keyof ArticleRow;
  return [...rows].sort((a, b) => {
    const av = a[key];
    const bv = b[key];
    if (av === bv) return 0;
    if (av === null || av === undefined) return 1;
    if (bv === null || bv === undefined) return -1;
    if (typeof av === "number" && typeof bv === "number")
      return (av - bv) * dir;
    return String(av).localeCompare(String(bv)) * dir;
  });
}

export function ArticlesTab({
  search,
  setSearch,
}: {
  search: KbRagSearch;
  setSearch: (next: Partial<KbRagSearch>) => void;
}) {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [sort, setSort] = useState<DataTableSort | null>({
    key: "hubspot_updated_at",
    dir: "desc",
  });
  const [offset, setOffset] = useState(0);
  const [expanded, setExpanded] = useState(false);

  const overview = useKbOverview();
  const { data, isPending, error } = useKbArticles({
    category: search.cat,
    status,
    q,
  });
  const detail = useKbArticle(search.article);

  const statusOptions = useMemo(() => {
    const seen = new Set(KNOWN_STATUSES);
    for (const s of overview.data?.article_statuses ?? []) seen.add(s.value);
    return [...seen];
  }, [overview.data]);

  const rows = useMemo(
    () => sortRows(data?.articles ?? [], sort),
    [data, sort],
  );
  const page = rows.slice(offset, offset + PAGE);

  const columns: DataTableColumn<ArticleRow>[] = [
    {
      key: "title",
      header: "Article",
      sortable: true,
      render: (r) => (
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="truncate font-medium" title={r.title ?? undefined}>
              {r.title ?? "Untitled"}
            </span>
            {r.is_internal ? <Badge variant="warning">internal</Badge> : null}
            {r.internal_marker_conflict ? (
              <Badge
                variant="info"
                title={
                  r.internal_marker_conflict === "category_only"
                    ? "Withheld from the agent because of its category, though the title does not say Internal. Check it really is internal."
                    : "Withheld because the title says Internal, though its category is not an internal one."
                }
              >
                marker conflict
              </Badge>
            ) : null}
          </div>
          <LinkOut href={r.url} size="xs" className="max-w-[28rem] truncate">
            {r.url?.replace(/^https?:\/\//, "") ?? "no url"}
          </LinkOut>
        </div>
      ),
      className: "max-w-[32rem]",
    },
    {
      key: "category",
      header: "Category",
      sortable: true,
      render: (r) => (
        <span className="text-sm" title={r.category_path.join(" › ")}>
          {r.category ?? "—"}
          {r.category_path.length > 1 ? (
            <span className="text-muted-foreground">
              {" "}
              › {r.category_path.slice(1).join(" › ")}
            </span>
          ) : null}
        </span>
      ),
    },
    {
      key: "hubspot_updated_at",
      header: "Updated in HubSpot",
      sortable: true,
      render: (r) => (
        <span className="tabular-nums">{day(r.hubspot_updated_at)}</span>
      ),
    },
    {
      key: "crawl_date",
      header: "Crawled",
      sortable: true,
      render: (r) => <span className="tabular-nums">{day(r.crawl_date)}</span>,
    },
    {
      key: "chunk_count",
      header: "Sections",
      sortable: true,
      align: "right",
      numeric: true,
      render: (r) =>
        r.chunk_count === null ? "—" : r.chunk_count.toLocaleString(),
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      render: (r) => (r.status ? <StatusBadge value={r.status} /> : "—"),
    },
  ];

  if (error) {
    return (
      <Alert variant="danger">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Could not load articles</AlertTitle>
        <AlertDescription>
          {error instanceof Error ? error.message : String(error)}
        </AlertDescription>
      </Alert>
    );
  }

  const source = data?.source;

  return (
    <div className="flex flex-col gap-4">
      {data && !data.pipeline_available ? (
        <Alert variant="warning">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>
            Showing the index, not the pipeline&rsquo;s article table
          </AlertTitle>
          <AlertDescription>
            The status API is unavailable
            {data.reason ? ` (${data.reason})` : ""}, so this list is read
            straight from the search index. Every article here IS indexed; what
            is missing is the pending / changed / deleted detail the pipeline
            keeps.
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <CategorySelect
          value={search.cat}
          onChange={(cat) => {
            setOffset(0);
            setSearch({ cat });
          }}
        />
        <Select
          value={status ?? ANY}
          onValueChange={(v) => {
            setOffset(0);
            setStatus(v === ANY ? undefined : v);
          }}
        >
          <SelectTrigger className="w-56">
            <SelectValue placeholder="Any status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Any status</SelectItem>
            {statusOptions.map((s) => (
              <SelectItem key={s} value={s}>
                <StatusBadge value={s} />
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isPending ? (
        <Skeleton className="h-96" />
      ) : (
        <DataTable<ArticleRow>
          columns={columns}
          rows={page}
          rowKey={(r) => r.document_id}
          sort={sort}
          onSortChange={setSort}
          search={q}
          onSearchChange={(v) => {
            setOffset(0);
            setQ(v);
          }}
          searchPlaceholder="Search titles and URLs"
          pagination={{ limit: PAGE, offset, count: rows.length }}
          onPageChange={setOffset}
          onRowClick={(r) => setSearch({ article: r.document_id })}
          selectedKey={search.article ?? null}
          emptyMessage="No articles match. Clear a filter, or check the Runs tab if the index is empty."
          toolbar={
            <span className="text-xs text-muted-foreground">
              {rows.length.toLocaleString()} article
              {rows.length === 1 ? "" : "s"}
              {source === "pipeline" && data?.generated_at
                ? ` · pipeline table from ${fmtDateTime(data.generated_at)}`
                : source === "qdrant"
                  ? " · from the index"
                  : ""}
            </span>
          }
        />
      )}

      <EntityDrawer
        open={Boolean(search.article)}
        onOpenChange={(open) => {
          if (!open) {
            setExpanded(false);
            setSearch({ article: undefined });
          }
        }}
        expanded={expanded}
        onExpandedChange={setExpanded}
        title={
          detail.data?.title ?? (detail.isPending ? "Loading…" : "Article")
        }
        subtitle={
          detail.data ? (
            <span className="flex flex-wrap items-center gap-2">
              {detail.data.category_path.length ? (
                <span>{detail.data.category_path.join(" › ")}</span>
              ) : null}
              <LinkOut href={detail.data.url} size="xs">
                open in HubSpot
              </LinkOut>
            </span>
          ) : undefined
        }
      >
        {detail.isPending ? (
          <div className="flex flex-col gap-3 p-4">
            <Skeleton className="h-6 w-2/3" />
            <Skeleton className="h-40" />
          </div>
        ) : detail.error ? (
          <div className="p-4">
            <Alert variant="danger">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Not in the index</AlertTitle>
              <AlertDescription>
                {detail.error instanceof Error
                  ? detail.error.message
                  : String(detail.error)}
                <div className="mt-1 text-xs">
                  The pipeline lists it but the search index has no record — it
                  is pending its first index, was excluded as internal, or was
                  just deleted.
                </div>
              </AlertDescription>
            </Alert>
          </div>
        ) : detail.data ? (
          <div className="flex flex-col gap-4 p-4">
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-4">
              <Meta
                label="Updated in HubSpot"
                value={day(detail.data.hubspot_updated_at)}
              />
              <Meta label="Crawled" value={day(detail.data.crawl_date)} />
              <Meta
                label="Indexed"
                value={fmtDateTime(detail.data.indexed_at)}
              />
              <Meta
                label="Sections · words"
                value={`${detail.data.chunk_count ?? "—"} · ${detail.data.word_count?.toLocaleString() ?? "—"}`}
              />
            </dl>
            <p className="text-xs text-muted-foreground">
              This is the article exactly as indexed — what the agent reads. If
              it differs from HubSpot, the next nightly run will pick the change
              up.
            </p>
            {detail.data.content_md ? (
              <MarkdownView source={detail.data.content_md} />
            ) : (
              <p className="text-sm italic text-muted-foreground">
                No body stored for this article.
              </p>
            )}
          </div>
        ) : null}
      </EntityDrawer>
    </div>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="tabular-nums">{value}</dd>
    </div>
  );
}
